import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as serviceWorker from "../../serviceWorker";
import { createDB } from "../../actions/vtrIndexedDB";

const ServiceWorkerContext = createContext();

export const ServiceWorkerProvider = ({ children }) => {
  const [waitingServiceWorker, setWaitingServiceWorker] = useState(null);
  const [isUpdateAvailable, setUpdateAvailable] = useState(false);
  const [isCheckingForUpdate, setIsCheckingForUpdate] = useState(false);
  const [noUpdateAvailable, setNoUpdateAvailable] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    serviceWorker.register({
      onUpdate: (registration) => {
        setWaitingServiceWorker(registration.waiting);
        setUpdateAvailable(true);
        setNoUpdateAvailable(false);
      },
      onWaiting: (waiting) => {
        setWaitingServiceWorker(waiting);
        setUpdateAvailable(true);
        setNoUpdateAvailable(false);
      },
    });
  }, []);

  useEffect(() => {
    // We setup an event listener to automatically reload the page
    // after the Service Worker has been updated, this will trigger
    // on all the open tabs of our application, so that we don't leave
    // any tab in an incosistent state
    if (waitingServiceWorker) {
      waitingServiceWorker.addEventListener("statechange", (event) => {
        if (event.target.state === "activated") {
          createDB().then(() => {
            window.location.reload();
          });
        }
      });
    }
  }, [waitingServiceWorker]);

  const checkForUpdate = useCallback(async () => {
    setIsCheckingForUpdate(true);
    setNoUpdateAvailable(false);
    setUpdateError(null);

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();

        // Check the result. If no worker is waiting or installing, no update was found.
        const updatedRegistration = await navigator.serviceWorker.getRegistration();
        if (!updatedRegistration.installing && !updatedRegistration.waiting) {
          setNoUpdateAvailable(true);
          // Hide the message automatically after a few seconds for better UX.
          setTimeout(() => setNoUpdateAvailable(false), 4000);
        }
      }
    } catch (error) {
      console.error("Error during manual service worker update check:", error);
      setUpdateError("Update check failed. Please check your network connection.");
      setTimeout(() => setUpdateError(null), 5000);
    } finally {
      setIsCheckingForUpdate(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      isUpdateAvailable,
      isCheckingForUpdate,
      noUpdateAvailable,
      updateError,
      updateAssets: () => {
        if (waitingServiceWorker) {
          // We send the SKIP_WAITING message to tell the Service Worker
          // to update its cache and flush the old one
          waitingServiceWorker.postMessage({ type: "SKIP_WAITING" });
        }
      },
      checkForUpdate,
    }),
    [
      isUpdateAvailable,
      waitingServiceWorker,
      isCheckingForUpdate,
      noUpdateAvailable,
      updateError,
      checkForUpdate,
    ]
  );

  return (
    <ServiceWorkerContext.Provider value={value}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};

export const useServiceWorker = () => {
  return useContext(ServiceWorkerContext);
};
